import React, { useContext, useEffect, useMemo, useState } from "react";
import { isNil, range } from "lodash";
import Collapsible from "react-collapsible";
import { useFormContext } from "react-hook-form";
import { getDocumentUploaderDefaults } from "common/services/formBuilderService";
import {
  Attachment,
  attachmentsAreCompatible,
} from "common/utils/customObjects";
import { spacing } from "../../../../../../../stitches.config";
import { AuthContext } from "../../../../../../Authorization/AuthContext";
import { BANNER_TYPES } from "../../../../../../Common/__styles__/Banner";
import { FlexRow } from "../../../../../../Common/__styles__/Layout";
import Banner from "../../../../../../Common/Banner";
import { ButtonLink } from "../../../../../../Common/ButtonLink";
import { Label, Select } from "../../../../../../Inputs";
import { Text, Textarea } from "../../../../../../Inputs/react-hook-form";
import { SelectedSubmissionsBuilderField } from "../../reducer";
import {
  SubmissionsBuilderFieldSettings,
  SubmissionsBuilderFormDataStructure,
} from "../../types";
import { WidgetSettingsContainer } from "./__styles__/WidgetSettings";
import {
  DisplaySettingsSection,
  FieldPermissionsConfig,
  FieldSettingConfig,
} from "./shared";

const getMinMaxOptions = ({
  limit,
  isMin,
}: {
  limit: number;
  isMin: boolean;
}) => {
  const min = isMin ? 1 : limit;
  const max = isMin ? limit : 25;

  // range is non-inclusive so we need to add 1 to the max
  return range(min, max + 1).map(value => ({
    label: value.toString(),
    value: value,
  }));
};

const DocumentUploadFieldSettings = ({
  fieldPath,
  isModuleInput,
  readOnly,
}: SelectedSubmissionsBuilderField &
  SubmissionsBuilderFieldSettings & {
    isModuleInput: boolean;
  }) => {
  const { account } = useContext(AuthContext);
  const { getValues, register, setValue } =
    useFormContext<SubmissionsBuilderFormDataStructure>();

  // We cannot pass the result of `getValues` to `useState` directly
  // because it breaks some type inference downstream.
  const formValues = getValues([
    `${fieldPath}.accountDocumentType`,
    `${fieldPath}.minItems`,
    `${fieldPath}.maxItems`,
    `${fieldPath}.required`,
  ]);

  const [
    [
      initialAccountDocumentType,
      initialMinItems,
      initialMaxItems,
      initialAreFilesRequired,
    ],
  ] = useState(formValues);

  const [accountDocumentTypeId, setAccountDocumentTypeId] = useState<
    Maybe<string> | undefined
  >(initialAccountDocumentType);

  const [minItems, setMinItems] =
    useState<Maybe<number | undefined>>(initialMinItems);
  const [maxItems, setMaxItems] =
    useState<Maybe<number | undefined>>(initialMaxItems);
  const [show, toggleShow] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const submissionAttachments = getValues("attachments");

  const accountDocumentTypeOptions = useMemo(() => {
    return (
      account?.accountDocumentTypes
        .map(accountDocumentType => ({
          label: accountDocumentType.name,
          value: accountDocumentType.id,
          attachments: accountDocumentType.attachments,
        }))
        .filter(accountDocumentType =>
          attachmentsAreCompatible({
            documentTypeAttachments:
              accountDocumentType.attachments as Attachment[],
            submissionAttachments,
          }),
        ) ?? []
    );
  }, [account?.accountDocumentTypes, submissionAttachments]);

  const [areFilesRequired, setAreFilesRequired] = useState(
    initialAreFilesRequired,
  );

  useEffect(() => {
    if (areFilesRequired) {
      // If initialMinItems is 0 or null/undefined, set minItems to 1
      setMinItems(initialMinItems || 1);
    } else {
      // Previously, if the document field was not required, minItems was set to null/undefined. However, the rjsf->intermediary
      // translation would treat undefined as 0. Then, if a change happened to the form without opening settings for document upload field,
      // it would result in a schema diff, because minItems would change from undefined to 0. By checking the value of initialMinItems,
      // we can have backwards compatability by not magically changing minItems (and therefore not creating a new STVersion) when
      // initialMinItems is "empty" (0 or null/undefined).
      if (isNil(initialMinItems)) {
        setMinItems(initialMinItems);
      } else {
        // If the document field is not required, minItems is 0 (similar to tags field)
        setMinItems(0);
      }
    }
  }, [areFilesRequired, initialMinItems]);

  useEffect(() => {
    // This is to respect existing values
    if (isInitialLoad) {
      setIsInitialLoad(false);
      return;
    }

    const castMaxItems = maxItems ?? 15;
    const castMinItems = minItems ?? 0;
    const accountDocumentType = account!.accountDocumentTypes.find(
      accountDocumentType => accountDocumentType.id === accountDocumentTypeId,
    );
    const defaults = getDocumentUploaderDefaults({
      allowedMimeTypes: accountDocumentType?.allowedMimeTypes,
      documentTypeName:
        accountDocumentType?.documentType?.name ?? accountDocumentType?.name,
      maxItems: castMaxItems,
      minItems: castMinItems,
    });

    Object.entries(defaults).forEach(([key, value]) => {
      setValue(`${fieldPath}.${key as keyof typeof defaults}`, value, {
        shouldValidate: true,
      });
    });
  }, [
    accountDocumentTypeId,
    maxItems,
    minItems,
    account,
    fieldPath,
    isInitialLoad,
    setValue,
  ]);

  useEffect(() => {
    if (minItems && maxItems && minItems > maxItems) {
      setMinItems(maxItems);
    }
  }, [minItems, maxItems]);

  useEffect(() => {
    setValue(`${fieldPath}.minItems`, minItems ?? undefined, {
      shouldValidate: true,
    });
  }, [minItems, fieldPath, setValue]);

  useEffect(() => {
    setValue(`${fieldPath}.maxItems`, maxItems ?? undefined, {
      shouldValidate: true,
    });
  }, [maxItems, fieldPath, setValue]);

  const trigger = (
    <div style={{ marginTop: spacing.s.value, marginBottom: spacing.m.value }}>
      <ButtonLink
        styleVariant="primary"
        size="medium"
        rightIconName={show ? "chevron-up" : "chevron-down"}
        onClick={e => {
          // Prevent default navigation behavior since this is used as a collapsible trigger
          e?.preventDefault();
        }}
      >
        Advanced settings
      </ButtonLink>
    </div>
  );

  if (!accountDocumentTypeOptions.length) {
    // TODO: add links to settings tab and Forest Admin
    return (
      <Banner type={BANNER_TYPES.INFO}>
        No compatible document types found. Please edit object type in the
        settings tab or update attachments for document types in Forest Admin.
      </Banner>
    );
  }

  return (
    <WidgetSettingsContainer>
      <FieldSettingConfig
        fieldPath={fieldPath}
        isModuleInput={isModuleInput}
        isFormulaField={false}
        onRequiredChange={evt => setAreFilesRequired(evt.target.checked)}
        readOnly={readOnly}
      />
      <div>
        <Label text={"Helper text"} htmlFor={`${fieldPath}.helperText`} />
        <Text {...register(`${fieldPath}.helperText`)} disabled={readOnly} />
      </div>
      <div>
        <Label
          text={"Document type"}
          htmlFor={`${fieldPath}.accountDocumentType`}
          required
        />
        <Select
          name={`${fieldPath}.accountDocumentType`}
          options={accountDocumentTypeOptions}
          value={accountDocumentTypeId}
          disabled={readOnly}
          onChange={value => {
            setValue(`${fieldPath}.accountDocumentType`, value ?? undefined, {
              shouldValidate: true,
            });
            setAccountDocumentTypeId(value);
          }}
        />
      </div>
      <FlexRow style={{ gap: 16 }}>
        <div style={{ flexBasis: "50%" }}>
          <Label
            text={"File upload minimum"}
            required={areFilesRequired}
            htmlFor={`${fieldPath}.minItems`}
          />
          <Select
            name={`${fieldPath}.minItems`}
            options={getMinMaxOptions({ isMin: true, limit: maxItems ?? 15 })}
            value={minItems}
            onChange={value => {
              setMinItems(value);
            }}
            disabled={!areFilesRequired || readOnly}
          />
        </div>
        <div style={{ flexBasis: "50%" }}>
          <Label
            text={"File upload maximum"}
            htmlFor={`${fieldPath}.maxItems`}
            required
          />
          <Select
            name={`${fieldPath}.maxItems`}
            options={getMinMaxOptions({ isMin: false, limit: minItems ?? 0 })}
            value={maxItems}
            onChange={value => {
              setMaxItems(value);
            }}
            disabled={readOnly}
          />
        </div>
      </FlexRow>
      {!!accountDocumentTypeId && (
        <Collapsible
          trigger={trigger}
          transitionTime={100}
          onOpen={() => {
            toggleShow(true);
          }}
          onClose={() => {
            toggleShow(false);
          }}
        >
          <WidgetSettingsContainer>
            <div>
              <Label
                text={"File type description"}
                htmlFor={`${fieldPath}.fileTypeDescription`}
                required
              />
              <Textarea
                {...register(`${fieldPath}.fileTypeDescription`)}
                disabled={readOnly}
              />
            </div>
            <div>
              <Label
                text={"File quantity description"}
                htmlFor={`${fieldPath}.fileQuantityDescription`}
                required
              />
              <Textarea
                {...register(`${fieldPath}.fileQuantityDescription`)}
                disabled={readOnly}
              />
            </div>
            <div>
              <Label
                text={"File size description"}
                htmlFor={`${fieldPath}.fileSizeDescription`}
                required
              />
              <Textarea
                {...register(`${fieldPath}.fileSizeDescription`)}
                disabled={readOnly}
              />
            </div>
            <div>
              <Label
                text={"Document type abbreviation"}
                htmlFor={`${fieldPath}.documentTypeAbbreviation`}
                required
              />
              <Text
                {...register(`${fieldPath}.documentTypeAbbreviation`)}
                disabled={readOnly}
              />
            </div>
            <div>
              <Label
                text={"Empty files message"}
                htmlFor={`${fieldPath}.emptyFilesMessage`}
              />
              <Text
                {...register(`${fieldPath}.emptyFilesMessage`)}
                disabled={readOnly}
              />
            </div>
          </WidgetSettingsContainer>
        </Collapsible>
      )}
      <FieldPermissionsConfig fieldPath={fieldPath} />
      <DisplaySettingsSection fieldPath={fieldPath} readOnly={readOnly} />
    </WidgetSettingsContainer>
  );
};

DocumentUploadFieldSettings.displayName = "DocumentUploadFieldSettings";

export default DocumentUploadFieldSettings;
